import clsx from 'clsx';
import {
    UserCircleIcon,
    ChevronDownIcon,
    UserIcon,
    ExclamationTriangleIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';
import type { ResponsibleVacationGroup } from '../../../models/vacationClientView.types';
import {
    RESPONSIBLE_SECTION_LABEL,
    RESPONSIBLE_PILL_LABELS,
    RESPONSIBLE_PILL_HINTS,
    RESPONSIBLE_PEOPLE_PILL_CLASS,
    RESPONSIBLE_PENDING_PILL_CLASS,
    RESPONSIBLE_OVERLAP_PILL_CLASS,
} from '../vacationClientPanel.definitions';

export interface ResponsibleSectionHeaderProps {
    responsible: ResponsibleVacationGroup;
    isOpen: boolean;
    controlsId: string;
    onToggle: () => void;
}

export function ResponsibleSectionHeader({
    responsible,
    isOpen,
    controlsId,
    onToggle,
}: ResponsibleSectionHeaderProps) {
    return (
        <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={controlsId}
            data-tour="conflicts-responsible-header"
            onClick={onToggle}
            className="flex w-full items-center justify-between gap-3 flex-wrap bg-raro-gray-50 px-4 py-3 text-left hover:bg-raro-gray-100 transition-colors"
        >
            <span className="flex items-center gap-2 min-w-0">
                <UserCircleIcon className="h-4 w-4 shrink-0 text-raro-gray-400" aria-hidden="true" />
                <span className="truncate text-sm font-semibold text-raro-gray-800 font-montserrat">
                    {RESPONSIBLE_SECTION_LABEL} {responsible.responsibleName}
                </span>
            </span>
            <span className="flex items-center gap-2">
                <span
                    title={RESPONSIBLE_PILL_HINTS.people}
                    className={clsx(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold font-montserrat',
                        RESPONSIBLE_PEOPLE_PILL_CLASS
                    )}
                >
                    <UserIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {RESPONSIBLE_PILL_LABELS.people(responsible.totalPeople)}
                </span>
                {responsible.pendingCount > 0 && (
                    <span
                        title={RESPONSIBLE_PILL_HINTS.pending}
                        className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold font-montserrat',
                            RESPONSIBLE_PENDING_PILL_CLASS
                        )}
                    >
                        <ClockIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                        {RESPONSIBLE_PILL_LABELS.pending(responsible.pendingCount)}
                    </span>
                )}
                {responsible.overlapCount > 0 && (
                    <span
                        title={RESPONSIBLE_PILL_HINTS.overlap}
                        className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold font-montserrat',
                            RESPONSIBLE_OVERLAP_PILL_CLASS
                        )}
                    >
                        <ExclamationTriangleIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                        {RESPONSIBLE_PILL_LABELS.overlap(responsible.overlapCount)}
                    </span>
                )}
                <ChevronDownIcon
                    className={clsx('h-4 w-4 shrink-0 text-raro-gray-400 transition-transform', isOpen && 'rotate-180')}
                    aria-hidden="true"
                />
            </span>
        </button>
    );
}

export default ResponsibleSectionHeader;
